import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { LOGO_URL, SUPPORT_EMAIL } from "../constants/brand";
import { getHomePathForRole } from "../modules/registry";

export default function TrustPortalLoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(getHomePathForRole(user.role), { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login({ identifier: email.trim(), password });
    setLoading(false);
    if (!res.success) {
      setError(res.error);
      return;
    }
    navigate(getHomePathForRole(res.user.role), { replace: true });
  };

  return (
    <div className="min-h-screen bg-auth-bg flex overflow-hidden" data-testid="trust-portal-login-page">
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md" data-testid="trust-portal-login-card">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-24 h-24 object-contain mb-4 auth-glow" />
            <h1 className="font-serif text-2xl text-auth-cream tracking-tight text-center">IGC Trust Portal</h1>
            <p className="text-auth-muted text-sm mt-2 tracking-wide">Living Trust &amp; Asset Protection Client Access</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="trust-portal-login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-auth-muted mb-2 block">Email</label>
              <Input data-testid="trust-portal-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} title="The email address you signed up with for your Trust Portal account." className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-auth-muted mb-2 block">Password</label>
              <div className="relative">
                <Input data-testid="trust-portal-login-password-input" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} title="Your Trust Portal account password." className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-12 pr-11" placeholder="••••••••" />
                <button type="button" data-testid="trust-portal-toggle-password" onClick={() => setShowPassword(!showPassword)} title="Click to reveal or hide the password you're typing." className="absolute right-3 top-1/2 -translate-y-1/2 text-auth-muted hover:text-auth-gold">
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="text-auth-danger text-sm" data-testid="trust-portal-login-error">{error}</p>}
            <Button data-testid="trust-portal-login-submit-button" type="submit" disabled={loading} title="Signs you in to your Trust Portal dashboard." className="w-full h-12 bg-auth-gold hover:bg-auth-goldHover text-black font-medium">
              {loading ? "Signing in..." : "Enter Trust Portal"}
            </Button>
          </form>
          <p className="text-center text-sm text-auth-muted mt-6">
            New here? <Link to="/trust-portal/signup" data-testid="trust-portal-goto-signup-link" title="Start a brand new Trust & Asset Protection intake with IGC." className="text-auth-gold hover:text-auth-goldHover">Start your intake</Link>
          </p>
          <p className="text-center text-xs text-auth-muted mt-8">Need help? <span className="text-auth-gold">{SUPPORT_EMAIL}</span></p>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 relative items-center justify-center bg-auth-surface overflow-hidden" data-testid="trust-portal-login-shield-panel">
        <div className="absolute inset-0 opacity-[0.14]" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #d9b44a 0, transparent 55%)" }} />
        <img src={LOGO_URL} alt="" className="w-[85%] max-w-2xl opacity-[0.22] select-none" style={{ filter: "brightness(0.7)" }} draggable="false" />
        <ShieldCheck size={28} weight="duotone" className="absolute top-10 right-10 text-auth-gold/40" />
      </div>
    </div>
  );
}
